import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Clock, X } from 'lucide-react';
import { getCookie, requestNotificationsRefresh } from '../utils/utils';
import { useCaps } from '../contexts/CapabilitiesContext';
import {
  isAiPipelinePurchaseProduct,
  type PurchaseProduct,
} from '../utils/marketplaceProducts';

export type { PurchaseProduct };

interface ProjectOption {
  id: number;
  name: string;
  markets?: string[];
}

interface BlueprintOption {
  id: number;
  name: string;
  job_to_be_done?: string;
  tension?: string;
  market?: string;
}

interface RecipeCardTabOption {
  id: number;
  tab_name: string;
}

interface PurchaseRequestModalProps {
  open: boolean;
  onClose: () => void;
  product: PurchaseProduct | null;
  selectedCurrency?: string;
  onViewOrderHistory?: () => void;
}

type ModalView = 'form' | 'submitted';

const inputClassName =
  'w-full h-10 rounded-lg border border-[#E8E8E8] bg-white px-3 text-[14px] text-[#0C0B0B] placeholder:text-[#9A9A9A] focus:outline-none focus:ring-1 focus:ring-[#674fa3] disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed';

const selectClassName =
  'w-full h-10 rounded-lg border border-[#E8E8E8] bg-white px-3 text-[14px] text-[#626262] focus:outline-none focus:ring-1 focus:ring-[#674fa3] appearance-none';

const NEXT_STEPS = [
  'A strategist will review your request.',
  "We'll contact you if any additional information is needed.",
  'Your project will be scheduled.',
  "You'll receive a notification when your results are ready.",
];

function isInnovationPlatformBlueprint(blueprint: BlueprintOption): boolean {
  return Boolean(blueprint.job_to_be_done?.trim() && blueprint.tension?.trim());
}

function formatBlueprintLabel(blueprint: BlueprintOption): string {
  const job = blueprint.job_to_be_done?.trim();
  const tension = blueprint.tension?.trim();
  if (job && tension) return `${job} / ${tension}`;
  if (job) return job;
  return blueprint.name || `Platform #${blueprint.id}`;
}

async function fetchBlueprintRows(projectId: number): Promise<BlueprintOption[]> {
  const response = await fetch(`/api/blueprints/?project=${projectId}&uses_new_workflow=true`, {
    credentials: 'include',
  });
  if (!response.ok) return [];
  const data = await response.json();
  const rows = Array.isArray(data) ? data : data.results || [];
  return rows.map((row: BlueprintOption) => ({
    id: row.id,
    name: row.name || `Platform #${row.id}`,
    job_to_be_done: row.job_to_be_done,
    tension: row.tension,
    market: row.market,
  }));
}

async function fetchJobsForProjectMarket(projectId: number, market: string): Promise<string[]> {
  const platforms = await fetchBlueprintRows(projectId);
  const jobs = new Set<string>();

  for (const platform of platforms) {
    if (platform.market && platform.market !== market) continue;
    if (isInnovationPlatformBlueprint(platform)) continue;

    if (platform.job_to_be_done?.trim()) {
      jobs.add(platform.job_to_be_done.trim());
    }

    try {
      const response = await fetch(`/api/blueprints/${platform.id}/jtbd-latest/`, {
        credentials: 'include',
      });
      if (!response.ok) continue;
      const data = await response.json();
      const statements = Array.isArray(data.jtbd_statements) ? data.jtbd_statements : [];
      for (const statement of statements) {
        if (typeof statement === 'string' && statement.trim()) {
          jobs.add(statement.trim());
        }
      }
    } catch {
      // ignore per-platform failures
    }
  }

  return Array.from(jobs);
}

async function fetchRecipeCardTabs(blueprintId: number): Promise<RecipeCardTabOption[]> {
  const response = await fetch(`/api/blueprints/${blueprintId}/recipe-card-tabs/`, {
    credentials: 'include',
  });
  if (!response.ok) return [];
  const data = await response.json();
  const rows = Array.isArray(data) ? data : [];
  return rows.map((row: RecipeCardTabOption) => ({
    id: row.id,
    tab_name: row.tab_name || `Recipe Card #${row.id}`,
  }));
}

function purchasePermissionError(
  product: PurchaseProduct | null | undefined,
  canAiGenerate: boolean,
  canMarketplaceOrder: boolean
): string | null {
  if (!product) return null;
  if (product.service_type === 'AI' && !canAiGenerate) {
    return 'You do not have permission to order AI products';
  }
  if (
    (product.service_type === 'Catalyx' || product.service_type === 'AI Off-Platform') &&
    !canMarketplaceOrder
  ) {
    return 'You do not have permission to order Catalyx marketplace products';
  }
  return null;
}

const PurchaseRequestModal: React.FC<PurchaseRequestModalProps> = ({
  open,
  onClose,
  product,
  selectedCurrency = 'CHF',
  onViewOrderHistory,
}) => {
  const { can } = useCaps();
  const canAiGenerate = can('ai_generate');
  const canMarketplaceOrder = can('marketplace_order');
  const [view, setView] = useState<ModalView>('form');
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [newProjectName, setNewProjectName] = useState('');
  const [selectedMarkets, setSelectedMarkets] = useState<string[]>([]);
  const [marketsOpen, setMarketsOpen] = useState(false);
  const marketsRef = useRef<HTMLDivElement>(null);

  const [jobOptions, setJobOptions] = useState<string[]>([]);
  const [selectedJobToBeDone, setSelectedJobToBeDone] = useState('');
  const [isLoadingJobs, setIsLoadingJobs] = useState(false);

  const [blueprintOptions, setBlueprintOptions] = useState<BlueprintOption[]>([]);
  const [selectedBlueprintId, setSelectedBlueprintId] = useState<number | null>(null);
  const [isLoadingBlueprints, setIsLoadingBlueprints] = useState(false);

  const [recipeCardTabOptions, setRecipeCardTabOptions] = useState<RecipeCardTabOption[]>([]);
  const [selectedRecipeCardTabId, setSelectedRecipeCardTabId] = useState<number | null>(null);
  const [isLoadingRecipeTabs, setIsLoadingRecipeTabs] = useState(false);

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const useSingleMarket = isAiPipelinePurchaseProduct(product);
  const allowsDraftProject =
    product?.service_type === 'Catalyx' || product?.service_type === 'AI Off-Platform';
  const workspaceKey = product?.workspace_key ?? '';
  const offersAllOfTheAbove =
    workspaceKey === 'jobs_identifier' || workspaceKey === 'tension_identifier';
  const allOfTheAboveLabel = 'All of the above';
  const needsJobDropdown = useSingleMarket && workspaceKey === 'tension_identifier';
  const needsRecipeDropdowns = useSingleMarket && workspaceKey === 'recipe_card_builder';
  const permissionError = purchasePermissionError(product, canAiGenerate, canMarketplaceOrder);

  const isCreatingNewProject = newProjectName.trim().length > 0;
  const hasExistingProjectSelected = selectedProjectId != null;

  const selectedProject = useMemo(
    () => projects.find((project) => project.id === selectedProjectId) ?? null,
    [projects, selectedProjectId]
  );

  const projectMarkets = useMemo(() => {
    const markets = selectedProject?.markets;
    return Array.isArray(markets) ? markets : [];
  }, [selectedProject]);

  const selectedMarket = selectedMarkets[0] ?? '';

  const allMarketsSelected =
    projectMarkets.length > 0 && projectMarkets.every((market) => selectedMarkets.includes(market));

  const canSend = useMemo(() => {
    if (purchasePermissionError(product, canAiGenerate, canMarketplaceOrder)) return false;
    if (allowsDraftProject && isCreatingNewProject) {
      return newProjectName.trim().length > 0;
    }
    if (selectedProjectId == null || selectedMarkets.length === 0) return false;
    if (useSingleMarket && selectedMarkets.length !== 1) return false;
    if (needsJobDropdown && !selectedJobToBeDone.trim()) return false;
    if (needsRecipeDropdowns) {
      if (selectedBlueprintId == null || selectedRecipeCardTabId == null) return false;
    }
    return true;
  }, [
    product,
    canAiGenerate,
    canMarketplaceOrder,
    allowsDraftProject,
    isCreatingNewProject,
    newProjectName,
    selectedProjectId,
    selectedMarkets,
    useSingleMarket,
    needsJobDropdown,
    selectedJobToBeDone,
    needsRecipeDropdowns,
    selectedBlueprintId,
    selectedRecipeCardTabId,
  ]);

  const resetDependentFields = useCallback(() => {
    setSelectedJobToBeDone('');
    setJobOptions([]);
    setSelectedBlueprintId(null);
    setBlueprintOptions([]);
    setSelectedRecipeCardTabId(null);
    setRecipeCardTabOptions([]);
  }, []);

  useEffect(() => {
    if (!open) {
      setView('form');
      setSelectedProjectId(null);
      setNewProjectName('');
      setSelectedMarkets([]);
      setMarketsOpen(false);
      resetDependentFields();
      setIsSubmitting(false);
      setSubmitError(null);
      return;
    }

    setView('form');
    // Show buy-permission message as soon as Purchase opens the modal.
    setSubmitError(purchasePermissionError(product, canAiGenerate, canMarketplaceOrder));

    const fetchProjects = async () => {
      setIsLoadingProjects(true);
      try {
        const response = await fetch('/api/projects/?lifecycle=active', {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });
        if (response.ok) {
          const data = await response.json();
          const list = Array.isArray(data) ? data : data.results || [];
          setProjects(
            list.map((project: ProjectOption) => ({
              id: project.id,
              name: project.name,
              markets: Array.isArray(project.markets) ? project.markets : [],
            }))
          );
        }
      } catch (err) {
        console.error('Error fetching projects:', err);
      } finally {
        setIsLoadingProjects(false);
      }
    };

    fetchProjects();
  }, [open, product, canAiGenerate, canMarketplaceOrder, resetDependentFields]);

  useEffect(() => {
    if (!marketsOpen) return;
    const handleClick = (event: MouseEvent) => {
      if (marketsRef.current && !marketsRef.current.contains(event.target as Node)) {
        setMarketsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [marketsOpen]);

  useEffect(() => {
    if (!open || !needsJobDropdown || selectedProjectId == null || !selectedMarket) {
      setJobOptions([]);
      setSelectedJobToBeDone('');
      return;
    }

    let cancelled = false;
    const loadJobs = async () => {
      setIsLoadingJobs(true);
      try {
        const jobs = await fetchJobsForProjectMarket(selectedProjectId, selectedMarket);
        if (!cancelled) {
          setJobOptions(jobs);
          setSelectedJobToBeDone('');
        }
      } catch (err) {
        console.error('Error fetching jobs:', err);
        if (!cancelled) setJobOptions([]);
      } finally {
        if (!cancelled) setIsLoadingJobs(false);
      }
    };

    loadJobs();
    return () => {
      cancelled = true;
    };
  }, [open, needsJobDropdown, selectedProjectId, selectedMarket]);

  useEffect(() => {
    if (!open || !needsRecipeDropdowns || selectedProjectId == null) {
      setBlueprintOptions([]);
      setSelectedBlueprintId(null);
      return;
    }

    let cancelled = false;
    const loadBlueprints = async () => {
      setIsLoadingBlueprints(true);
      try {
        const rows = await fetchBlueprintRows(selectedProjectId);
        const filtered = selectedMarket
          ? rows.filter((row) => !row.market || row.market === selectedMarket)
          : rows;
        if (!cancelled) {
          setBlueprintOptions(filtered);
          setSelectedBlueprintId(null);
          setSelectedRecipeCardTabId(null);
          setRecipeCardTabOptions([]);
        }
      } catch (err) {
        console.error('Error fetching blueprints:', err);
        if (!cancelled) setBlueprintOptions([]);
      } finally {
        if (!cancelled) setIsLoadingBlueprints(false);
      }
    };

    loadBlueprints();
    return () => {
      cancelled = true;
    };
  }, [open, needsRecipeDropdowns, selectedProjectId, selectedMarket]);

  useEffect(() => {
    if (!open || !needsRecipeDropdowns || selectedBlueprintId == null) {
      setRecipeCardTabOptions([]);
      setSelectedRecipeCardTabId(null);
      return;
    }

    let cancelled = false;
    const loadTabs = async () => {
      setIsLoadingRecipeTabs(true);
      try {
        const tabs = await fetchRecipeCardTabs(selectedBlueprintId);
        if (!cancelled) {
          setRecipeCardTabOptions(tabs);
          setSelectedRecipeCardTabId(null);
        }
      } catch (err) {
        console.error('Error fetching recipe card tabs:', err);
        if (!cancelled) setRecipeCardTabOptions([]);
      } finally {
        if (!cancelled) setIsLoadingRecipeTabs(false);
      }
    };

    loadTabs();
    return () => {
      cancelled = true;
    };
  }, [open, needsRecipeDropdowns, selectedBlueprintId]);

  const handleProjectChange = (projectId: string) => {
    setMarketsOpen(false);
    resetDependentFields();
    setNewProjectName('');

    const id = projectId ? Number(projectId) : null;
    setSelectedProjectId(id);

    if (!id) {
      setSelectedMarkets([]);
      return;
    }

    const project = projects.find((item) => item.id === id);
    const markets = project && Array.isArray(project.markets) ? project.markets : [];
    setSelectedMarkets(markets.length === 1 ? [markets[0]] : []);
  };

  const handleNewProjectNameChange = (value: string) => {
    setNewProjectName(value);
    if (value.trim()) {
      setSelectedProjectId(null);
      setSelectedMarkets([]);
      setMarketsOpen(false);
      resetDependentFields();
    }
  };

  const handleSingleMarketChange = (market: string) => {
    setSelectedMarkets(market ? [market] : []);
    resetDependentFields();
  };

  const toggleMarket = (market: string) => {
    setSelectedMarkets((prev) =>
      prev.includes(market) ? prev.filter((item) => item !== market) : [...prev, market]
    );
    resetDependentFields();
  };

  const toggleAllMarkets = () => {
    if (allMarketsSelected) {
      setSelectedMarkets([]);
    } else {
      setSelectedMarkets([...projectMarkets]);
    }
    resetDependentFields();
  };

  const formatMarketList = (markets: string[]) => markets.join(', ');

  const marketsLabel = () => {
    if (!selectedProjectId) return 'Select market*';
    if (projectMarkets.length === 0) return 'No markets for this project';
    if (selectedMarkets.length === 0) return 'Select market*';
    if (projectMarkets.length === 1) return projectMarkets[0];
    if (allMarketsSelected) {
      return `All markets: ${formatMarketList(projectMarkets)}`;
    }
    if (selectedMarkets.length === 1) return selectedMarkets[0];
    return `${selectedMarkets.length} markets selected: ${formatMarketList(selectedMarkets)}`;
  };

  const showAllMarketsOption = projectMarkets.length > 1;

  const handleSendRequest = () => {
    if (!canSend || !product || isSubmitting) return;
    const denied = purchasePermissionError(product, canAiGenerate, canMarketplaceOrder);
    if (denied) {
      setSubmitError(denied);
      return;
    }

    setSubmitError(null);
    setIsSubmitting(true);

    const submitOrder = (projectId: number | null) => {
      const marketsString = isCreatingNewProject ? '' : selectedMarkets.join(', ');
      const payload = {
        product: product.id,
        quantity: 1,
        markets: marketsString,
        user_selected_currency: selectedCurrency,
        project: projectId,
        ai_market: isCreatingNewProject ? '' : selectedMarket,
        ai_job_to_be_done: needsJobDropdown ? selectedJobToBeDone : '',
        ai_target_blueprint_id: needsRecipeDropdowns ? selectedBlueprintId : null,
        ai_recipe_card_tab_id: needsRecipeDropdowns ? selectedRecipeCardTabId : null,
      };

      void fetch('/api/orders/', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCookie('csrftoken') || '',
        },
        body: JSON.stringify(payload),
      })
        .then(async (response) => {
          if (!response.ok) {
            let message = 'Failed to submit request.';
            try {
              const data = await response.json();
              if (typeof data === 'string') {
                message = data;
              } else if (data?.error) {
                message = data.error;
              } else if (data?.detail) {
                message = data.detail;
              } else if (data && typeof data === 'object') {
                const firstKey = Object.keys(data)[0];
                const firstValue = firstKey ? data[firstKey] : null;
                if (Array.isArray(firstValue) && firstValue[0]) {
                  message = String(firstValue[0]);
                }
              }
            } catch {
              // keep default message
            }
            setView('form');
            setSubmitError(message);
            return;
          }

          requestNotificationsRefresh();
        })
        .catch((err) => {
          console.error('Purchase request failed:', err);
          setView('form');
          setSubmitError('Failed to submit request. Please try again.');
        })
        .finally(() => {
          setIsSubmitting(false);
        });

      setView('submitted');
    };

    if (allowsDraftProject && isCreatingNewProject) {
      void fetch('/api/projects/', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCookie('csrftoken') || '',
        },
        body: JSON.stringify({
          name: newProjectName.trim(),
          lifecycle: 'draft',
        }),
      })
        .then(async (response) => {
          if (!response.ok) {
            let message = 'Failed to create project.';
            try {
              const data = await response.json();
              message =
                data?.detail ||
                data?.error ||
                (typeof data?.name?.[0] === 'string' ? data.name[0] : message);
            } catch {
              // keep default
            }
            setSubmitError(message);
            setIsSubmitting(false);
            return;
          }
          const project = await response.json();
          submitOrder(project.id);
        })
        .catch((err) => {
          console.error('Draft project create failed:', err);
          setSubmitError('Failed to create project. Please try again.');
          setIsSubmitting(false);
        });
      return;
    }

    submitOrder(selectedProjectId);
  };

  if (!open || !product) return null;

  return (
    <div
      className="fixed inset-0 z-[15400] bg-black/45 backdrop-blur-sm flex items-center justify-center p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="purchase-request-title"
      onClick={onClose}
    >
      <div
        className="w-[734px] rounded-2xl bg-white shadow-2xl flex flex-col"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between px-8 pt-8 pb-4">
          <h2 id="purchase-request-title" className="text-[20px] font-bold text-[#0C0B0B]">
            Request to purchase
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-gray-900 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors shrink-0"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {view === 'form' ? (
          <>
            <div className="px-8 flex flex-col gap-4">
              <div className="rounded-lg border border-[#E8E8E8] bg-white px-5 py-4">
                <h3 className="text-[16px] font-bold text-[#6850a4] leading-snug">{product.name}</h3>
                {product.short_description && (
                  <p className="mt-2 text-[14px] font-normal leading-normal text-[#626262]">
                    {product.short_description}
                  </p>
                )}
                <p
                  className={`text-[14px] font-bold leading-none text-[#6850a4] ${
                    product.short_description ? 'mt-3' : 'mt-2'
                  }`}
                >
                  From {product.credits} credits · {product.duration_in_days} days
                </p>
              </div>

              {(permissionError || submitError) && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3">
                  <p className="text-[14px] text-red-700 leading-normal">
                    {permissionError || submitError}
                  </p>
                </div>
              )}

              <div>
                <p className="text-[14px] font-bold text-[#0C0B0B]">Project details*</p>
                {allowsDraftProject && (
                  <p className="mt-1 text-[14px] text-[#626262]">
                    You can either select an existing project or enter a name to create a new one.
                  </p>
                )}
                <div className="mt-3">
                  <label htmlFor="purchase-project" className="block text-[14px] font-bold text-[#0C0B0B] mb-2">
                    Select a project
                  </label>
                  <div className="relative">
                    <select
                      id="purchase-project"
                      value={selectedProjectId ?? ''}
                      onChange={(event) => handleProjectChange(event.target.value)}
                      disabled={isLoadingProjects || isCreatingNewProject}
                      className={`${selectClassName} pr-10 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed`}
                    >
                      <option value="">
                        {isLoadingProjects ? 'Loading projects...' : 'Select a project'}
                      </option>
                      {projects.map((project) => (
                        <option key={project.id} value={project.id}>
                          {project.name}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#626262]" />
                  </div>
                </div>

                {useSingleMarket ? (
                  <div className="mt-4">
                    <label htmlFor="purchase-market" className="block text-[14px] font-bold text-[#0C0B0B] mb-2">
                      Select market
                    </label>
                    <div className="relative">
                      <select
                        id="purchase-market"
                        value={selectedMarket}
                        onChange={(event) => handleSingleMarketChange(event.target.value)}
                        disabled={isCreatingNewProject || !selectedProjectId || projectMarkets.length === 0}
                        className={`${selectClassName} pr-10 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed`}
                      >
                        <option value="">
                          {!selectedProjectId
                            ? 'Select market*'
                            : projectMarkets.length === 0
                              ? 'No markets for this project'
                              : 'Select market*'}
                        </option>
                        {projectMarkets.map((market) => (
                          <option key={market} value={market}>
                            {market}
                          </option>
                        ))}
                        {offersAllOfTheAbove &&
                          projectMarkets.length > 1 &&
                          !projectMarkets.includes(allOfTheAboveLabel) && (
                            <option value={allOfTheAboveLabel}>{allOfTheAboveLabel}</option>
                          )}
                      </select>
                      <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#626262]" />
                    </div>
                  </div>
                ) : (
                  <div className="relative mt-4" ref={marketsRef}>
                    <p className="text-[14px] font-bold text-[#0C0B0B] mb-2">Select market</p>
                    <button
                      type="button"
                      onClick={() => {
                        if (selectedProjectId && projectMarkets.length > 1 && !isCreatingNewProject) {
                          setMarketsOpen((prev) => !prev);
                        }
                      }}
                      disabled={isCreatingNewProject || !selectedProjectId || projectMarkets.length === 0}
                      className={`${selectClassName} text-left flex items-center justify-between pr-3 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed ${
                        projectMarkets.length === 1 ? 'cursor-default' : ''
                      }`}
                    >
                      <span className="truncate">{marketsLabel()}</span>
                      <ChevronDown className="w-4 h-4 text-[#626262] shrink-0 ml-2" />
                    </button>

                    {marketsOpen && projectMarkets.length > 1 && !isCreatingNewProject && (
                      <div className="absolute z-10 mt-1 w-full rounded-lg border border-[#E8E8E8] bg-white shadow-lg py-2 max-h-48 overflow-y-auto">
                        {showAllMarketsOption && (
                          <>
                            <label className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={allMarketsSelected}
                                onChange={toggleAllMarkets}
                                className="rounded border-gray-300 text-[#674fa3] focus:ring-[#674fa3]"
                              />
                              <span className="text-[14px] text-[#0C0B0B] font-medium">All markets</span>
                            </label>
                            <div className="border-t border-[#E8E8E8] my-1" />
                          </>
                        )}
                        {projectMarkets.map((market) => (
                          <label
                            key={market}
                            className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedMarkets.includes(market)}
                              onChange={() => toggleMarket(market)}
                              className="rounded border-gray-300 text-[#674fa3] focus:ring-[#674fa3]"
                            />
                            <span className="text-[14px] text-[#626262]">{market}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {allowsDraftProject && (
                  <div className="mt-4">
                    <label
                      htmlFor="new-project-name"
                      className="block text-[14px] font-bold text-[#0C0B0B] mb-2"
                    >
                      Or create a new project
                    </label>
                    <input
                      id="new-project-name"
                      type="text"
                      value={newProjectName}
                      onChange={(event) => handleNewProjectNameChange(event.target.value)}
                      placeholder="e.g. Evening Wind-Down France"
                      disabled={hasExistingProjectSelected}
                      className={inputClassName}
                    />
                  </div>
                )}
              </div>

              {needsJobDropdown && (
                <div className="relative">
                  <select
                    value={selectedJobToBeDone}
                    onChange={(event) => setSelectedJobToBeDone(event.target.value)}
                    disabled={!selectedMarket || isLoadingJobs || jobOptions.length === 0}
                    className={`${selectClassName} pr-10 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed`}
                  >
                    <option value="">
                      {isLoadingJobs
                        ? 'Loading jobs...'
                        : !selectedMarket
                          ? 'Select a job to be done*'
                          : jobOptions.length === 0
                            ? 'No jobs available for this market'
                            : 'Select a job to be done*'}
                    </option>
                    {jobOptions.map((job) => (
                      <option key={job} value={job}>
                        {job}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#626262]" />
                </div>
              )}

              {needsRecipeDropdowns && (
                <>
                  <div className="relative">
                    <select
                      value={selectedBlueprintId ?? ''}
                      onChange={(event) => {
                        const value = event.target.value ? Number(event.target.value) : null;
                        setSelectedBlueprintId(value);
                        setSelectedRecipeCardTabId(null);
                        setRecipeCardTabOptions([]);
                      }}
                      disabled={!selectedProjectId || isLoadingBlueprints || blueprintOptions.length === 0}
                      className={`${selectClassName} pr-10 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed`}
                    >
                      <option value="">
                        {isLoadingBlueprints
                          ? 'Loading innovation platforms...'
                          : blueprintOptions.length === 0
                            ? 'No innovation platforms available'
                            : 'Select an innovation platform*'}
                      </option>
                      {blueprintOptions.map((blueprint) => (
                        <option key={blueprint.id} value={blueprint.id}>
                          {formatBlueprintLabel(blueprint)}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#626262]" />
                  </div>

                  <div className="relative">
                    <select
                      value={selectedRecipeCardTabId ?? ''}
                      onChange={(event) =>
                        setSelectedRecipeCardTabId(
                          event.target.value ? Number(event.target.value) : null
                        )
                      }
                      disabled={
                        selectedBlueprintId == null ||
                        isLoadingRecipeTabs ||
                        recipeCardTabOptions.length === 0
                      }
                      className={`${selectClassName} pr-10 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed`}
                    >
                      <option value="">
                        {isLoadingRecipeTabs
                          ? 'Loading recipe cards...'
                          : selectedBlueprintId == null
                            ? 'Select a recipe card*'
                            : recipeCardTabOptions.length === 0
                              ? 'No recipe cards available'
                              : 'Select a recipe card*'}
                      </option>
                      {recipeCardTabOptions.map((tab) => (
                        <option key={tab.id} value={tab.id}>
                          {tab.tab_name}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#626262]" />
                  </div>
                </>
              )}

              {submitError && !permissionError && (
                <p className="text-[14px] text-red-600 leading-normal">{submitError}</p>
              )}
            </div>

            <div className="px-8 pb-8 pt-6 flex justify-end">
              <button
                type="button"
                onClick={handleSendRequest}
                disabled={!canSend || isSubmitting}
                className={`px-8 py-2.5 rounded-full text-[14px] font-medium transition-colors ${
                  canSend && !isSubmitting
                    ? 'bg-[#674fa3] text-white hover:bg-[#5a4490]'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {isSubmitting ? 'Sending...' : 'Send request'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="px-8 pb-6 flex flex-col gap-4">
              <div>
                <h3 className="text-[16px] font-bold text-[#0C0B0B]">Request submitted</h3>
                <p className="mt-2 text-[14px] text-[#626262] leading-normal">
                  {useSingleMarket
                    ? "Thanks! We're processing your request and will notify you when your results are ready."
                    : 'Thanks! Your request has been sent to the Catalyx team.'}
                </p>
              </div>

              <div>
                <p className="text-[14px] font-bold text-[#0C0B0B] mb-2">What happens next?</p>
                <ol className="list-decimal list-inside space-y-1 text-[14px] text-[#626262] leading-normal">
                  {NEXT_STEPS.map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </ol>
              </div>

              <div className="border-t border-[#E8E8E8] pt-4">
                <h3 className="text-[16px] font-bold text-[#0C0B0B]">Estimated turnaround</h3>
                <p className="mt-2 text-[14px] text-[#626262]">Estimated turnaround</p>
                <div className="mt-1 flex items-center gap-2 text-[14px] text-[#626262]">
                  <Clock className="w-4 h-4 shrink-0" />
                  <span>3–5 business days</span>
                </div>
              </div>

              <p className="text-[14px] text-[#626262] leading-normal">
                You can track the status of this request anytime
                {onViewOrderHistory ? (
                  <>
                    {' '}
                    in{' '}
                    <button
                      type="button"
                      onClick={onViewOrderHistory}
                      className="underline text-[#3da3d4] hover:text-[#2d8ab8] transition-colors"
                    >
                      Order History.
                    </button>
                  </>
                ) : (
                  '.'
                )}
              </p>
            </div>

            <div className="px-8 pb-8 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2.5 rounded-full border-2 border-[#3da3d4] text-[#3da3d4] text-[14px] font-medium hover:bg-[#3da3d4]/5 transition-colors"
              >
                Back to Marketplace
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PurchaseRequestModal;
